import React, { useState, useEffect, useMemo } from 'react';
import {
    X, Lightbulb, AlertCircle, Loader, Package, CheckCircle, XCircle,
    ArrowRight, Wand2, AlertTriangle
} from 'lucide-react';
import {
    SuggestDestinationsResponse,
    ItemSuggestion,
    ExecuteBatchMoveResponse,
    BatchMoveSource,
    HeatmapMoveReason,
    HeatmapMovePriority,
    HEATMAP_MOVE_REASON_LABELS,
    HEATMAP_MOVE_PRIORITY_LABELS
} from './heatmapMoveTypes';

interface LineState {
    fromBinCode: string;
    itemNo: string;
    description1?: string;
    availableQuantity: number;
    selected: boolean;
    quantity: number;
    /** Currently selected destination. Defaults to first suggestion. Editable. */
    toBinCode: string;
    suggestions: { binCode: string; reason?: string; priority: number }[];
    noSuggestionsError?: string;
}

interface SuggestAndMoveModalProps {
    isOpen: boolean;
    fromBinCode: string;
    /** If 'empty-bin' → pre-selects all; if 'suggest' → also pre-selects all, user can deselect. */
    mode: 'suggest' | 'empty-bin';

    isLoading: boolean;
    suggestions: SuggestDestinationsResponse | null;
    loadError: string | null;

    isExecuting: boolean;
    executeError: string | null;
    executeResult: ExecuteBatchMoveResponse | null;

    onClose: () => void;
    onConfirm: (params: {
        sources: BatchMoveSource[];
        reason: HeatmapMoveReason;
        priority: HeatmapMovePriority;
        comment: string;
    }) => Promise<void>;
    /** Optional: user chose "Legg i plan" instead of "Utfør nå". */
    onAddToPlan?: (sources: BatchMoveSource[]) => void;
}

/**
 * Combined modal for "Foreslå plassering" and "Tøm lokasjon".
 *
 * Shows each item from the source bin with its suggested destination (from
 * PutAwayService). User can deselect items, adjust quantity, or override
 * destination before confirming.
 */
export const SuggestAndMoveModal: React.FC<SuggestAndMoveModalProps> = ({
    isOpen,
    fromBinCode,
    mode,
    isLoading,
    suggestions,
    loadError,
    isExecuting,
    executeError,
    executeResult,
    onClose,
    onConfirm,
    onAddToPlan
}) => {
    const [lines, setLines] = useState<LineState[]>([]);
    const [reason, setReason] = useState<HeatmapMoveReason>(
        mode === 'empty-bin' ? HeatmapMoveReason.Rebuild : HeatmapMoveReason.PickOptimization
    );
    const [priority, setPriority] = useState<HeatmapMovePriority>(HeatmapMovePriority.Normal);
    const [comment, setComment] = useState('');

    // Flatten suggestions into editable line state.
    useEffect(() => {
        if (!suggestions) {
            setLines([]);
            return;
        }
        const flat: LineState[] = suggestions.suggestions.map((s: ItemSuggestion) => {
            const hasSuggestion = s.destinations.length > 0;
            return {
                fromBinCode: s.fromBinCode,
                itemNo: s.itemNo,
                description1: s.description1,
                availableQuantity: s.availableQuantity,
                selected: hasSuggestion, // Can't select without a destination
                quantity: s.availableQuantity,
                toBinCode: hasSuggestion ? s.destinations[0].binCode : '',
                suggestions: s.destinations,
                noSuggestionsError: hasSuggestion ? undefined : (s.errorMessage || 'Ingen forslag funnet')
            };
        });
        setLines(flat);
    }, [suggestions]);

    const totals = useMemo(() => {
        const active = lines.filter(l => l.selected);
        return {
            lineCount: active.length,
            totalQuantity: active.reduce((sum, l) => sum + l.quantity, 0),
            totalLines: lines.length,
            linesWithoutSuggestion: lines.filter(l => !l.suggestions.length).length
        };
    }, [lines]);

    const canConfirm =
        !isLoading &&
        !isExecuting &&
        !executeResult &&
        totals.lineCount > 0 &&
        lines.every(l => !l.selected || (
            l.quantity > 0 &&
            l.quantity <= l.availableQuantity &&
            !!l.toBinCode &&
            l.toBinCode !== l.fromBinCode
        ));

    const handleToggle = (idx: number) => {
        setLines(prev => prev.map((l, i) =>
            i === idx && l.suggestions.length > 0 ? { ...l, selected: !l.selected } : l));
    };

    const handleQuantityChange = (idx: number, v: number) => {
        setLines(prev => prev.map((l, i) => {
            if (i !== idx) return l;
            const clamped = isNaN(v) || v < 0 ? 0 : Math.min(v, l.availableQuantity);
            return { ...l, quantity: clamped };
        }));
    };

    const handleDestinationChange = (idx: number, binCode: string) => {
        setLines(prev => prev.map((l, i) =>
            i === idx ? { ...l, toBinCode: binCode } : l));
    };

    const handleConfirm = async () => {
        if (!canConfirm) return;
        const sources: BatchMoveSource[] = lines
            .filter(l => l.selected && l.quantity > 0 && l.toBinCode)
            .map(l => ({
                fromBinCode: l.fromBinCode,
                itemNo: l.itemNo,
                quantity: l.quantity,
                toBinCodeOverride: l.toBinCode
            }));
        await onConfirm({ sources, reason, priority, comment: comment.trim() });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        {mode === 'empty-bin' ? (
                            <Package className="w-6 h-6 text-red-600" />
                        ) : (
                            <Lightbulb className="w-6 h-6 text-amber-500" />
                        )}
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">
                                {mode === 'empty-bin' ? 'Tøm lokasjon' : 'Foreslå plassering'}
                            </h2>
                            <div className="text-sm text-gray-600 mt-0.5">
                                Fra <span className="font-mono font-semibold">{fromBinCode}</span>
                                <span className="ml-2 text-xs text-gray-500">
                                    {mode === 'empty-bin'
                                        ? '— alt innhold flyttes til auto-foreslåtte destinasjoner'
                                        : '— anbefalinger fra regelmotoren'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isExecuting}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                        aria-label="Lukk"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {isLoading && (
                        <div className="flex items-center justify-center py-8">
                            <Loader className="w-6 h-6 animate-spin text-blue-600" />
                            <span className="ml-3 text-sm text-gray-600">Beregner forslag...</span>
                        </div>
                    )}

                    {loadError && !isLoading && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 flex items-start space-x-2">
                            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <span>{loadError}</span>
                        </div>
                    )}

                    {executeResult && (
                        <div className="space-y-3">
                            <div className={`border rounded-lg p-3 ${
                                executeResult.failedCount === 0
                                    ? 'bg-green-50 border-green-200 text-green-800'
                                    : executeResult.successCount === 0
                                      ? 'bg-red-50 border-red-200 text-red-800'
                                      : 'bg-amber-50 border-amber-200 text-amber-800'
                            }`}>
                                <div className="font-semibold text-sm mb-1">
                                    {executeResult.failedCount === 0
                                        ? `✓ Alle ${executeResult.successCount} linjer opprettet`
                                        : `Delvis: ${executeResult.successCount} lykkes, ${executeResult.failedCount} feilet`}
                                </div>
                                {executeResult.stcOrderNo && (
                                    <div className="text-xs">Ordre #{executeResult.stcOrderNo}</div>
                                )}
                            </div>
                            <table className="w-full text-xs">
                                <thead className="bg-gray-50 text-gray-600">
                                    <tr>
                                        <th className="px-2 py-1.5 text-left">Vare</th>
                                        <th className="px-2 py-1.5 text-left">Til</th>
                                        <th className="px-2 py-1.5 text-right">Mengde</th>
                                        <th className="px-2 py-1.5 text-left">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {executeResult.results.map((r, i) => (
                                        <tr key={i} className="border-t border-gray-100">
                                            <td className="px-2 py-1.5">{r.itemNo}</td>
                                            <td className="px-2 py-1.5 font-mono">{r.toBinCode}</td>
                                            <td className="px-2 py-1.5 text-right">{r.quantity}</td>
                                            <td className="px-2 py-1.5">
                                                {r.success ? (
                                                    <span className="text-green-700 flex items-center">
                                                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> OK
                                                    </span>
                                                ) : (
                                                    <span className="text-red-700 flex items-center" title={r.errorMessage}>
                                                        <XCircle className="w-3.5 h-3.5 mr-1" />
                                                        {r.errorMessage || 'Feilet'}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!isLoading && suggestions && !executeResult && (
                        <div className="space-y-4">
                            {totals.linesWithoutSuggestion > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 flex items-start space-x-2">
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>
                                        {totals.linesWithoutSuggestion} av {totals.totalLines} varer har ingen foreslått destinasjon
                                        og er deaktivert. Dette kan skyldes manglende master-data eller at bin-gruppen er full.
                                    </span>
                                </div>
                            )}

                            {lines.length === 0 ? (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-600">
                                    Lokasjonen er tom — ingenting å flytte.
                                </div>
                            ) : (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-sm font-semibold text-gray-700">
                                            {totals.lineCount} av {totals.totalLines} valgt · total {totals.totalQuantity} stk
                                        </div>
                                        <div className="flex items-center space-x-1 text-xs text-amber-700">
                                            <Wand2 className="w-3.5 h-3.5" />
                                            <span>Destinasjoner foreslått av regelmotoren</span>
                                        </div>
                                    </div>

                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                                                <tr>
                                                    <th className="px-2 py-1.5 w-8"></th>
                                                    <th className="px-2 py-1.5 text-left">Vare</th>
                                                    <th className="px-2 py-1.5 text-right">Tilgj.</th>
                                                    <th className="px-2 py-1.5 text-right w-20">Mengde</th>
                                                    <th className="px-2 py-1.5 text-left">Destinasjon</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lines.map((line, idx) => (
                                                    <tr key={`${line.itemNo}`}
                                                        className={`border-t border-gray-100 ${
                                                            !line.suggestions.length ? 'bg-yellow-50' :
                                                            !line.selected ? 'opacity-50' : ''
                                                        }`}>
                                                        <td className="px-2 py-1.5">
                                                            <input
                                                                type="checkbox"
                                                                checked={line.selected}
                                                                disabled={!line.suggestions.length}
                                                                onChange={() => handleToggle(idx)}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-1.5 text-xs">
                                                            <div className="font-mono">{line.itemNo}</div>
                                                            {line.description1 && (
                                                                <div className="text-gray-500 truncate max-w-xs">
                                                                    {line.description1}
                                                                </div>
                                                            )}
                                                            {line.noSuggestionsError && (
                                                                <div className="text-[10px] text-yellow-700 mt-0.5">
                                                                    {line.noSuggestionsError}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-2 py-1.5 text-right text-xs">
                                                            {line.availableQuantity}
                                                        </td>
                                                        <td className="px-2 py-1.5 text-right">
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={line.availableQuantity}
                                                                step="any"
                                                                value={line.quantity}
                                                                disabled={!line.selected}
                                                                onChange={e => handleQuantityChange(idx, parseFloat(e.target.value))}
                                                                className="w-16 border border-gray-300 rounded px-2 py-0.5 text-xs text-right disabled:bg-gray-50"
                                                            />
                                                        </td>
                                                        <td className="px-2 py-1.5">
                                                            {line.suggestions.length ? (
                                                                <div className="flex items-center space-x-2">
                                                                    <select
                                                                        value={line.toBinCode}
                                                                        disabled={!line.selected}
                                                                        onChange={e => handleDestinationChange(idx, e.target.value)}
                                                                        className="border border-gray-300 rounded px-2 py-0.5 text-xs font-mono disabled:bg-gray-50"
                                                                    >
                                                                        {line.suggestions.map((s, si) => (
                                                                            <option key={si} value={s.binCode}>
                                                                                {s.binCode}{s.reason ? ` (${s.reason})` : ''}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <ArrowRight className="w-3 h-3 text-gray-400" />
                                                                </div>
                                                            ) : (
                                                                <span className="text-xs text-yellow-700">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Årsak</label>
                                <select
                                    value={reason}
                                    onChange={e => setReason(parseInt(e.target.value, 10) as HeatmapMoveReason)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                >
                                    {Object.entries(HEATMAP_MOVE_REASON_LABELS).map(([v, label]) => (
                                        <option key={v} value={v}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Prioritet</label>
                                <div className="flex space-x-2">
                                    {Object.entries(HEATMAP_MOVE_PRIORITY_LABELS).map(([v, label]) => {
                                        const val = parseInt(v, 10) as HeatmapMovePriority;
                                        return (
                                            <button
                                                key={v}
                                                type="button"
                                                onClick={() => setPriority(val)}
                                                className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border ${
                                                    priority === val
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Kommentar <span className="text-gray-400 font-normal">(valgfri)</span>
                                </label>
                                <textarea
                                    value={comment}
                                    onChange={e => setComment(e.target.value)}
                                    rows={2}
                                    maxLength={200}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                                />
                            </div>

                            {executeError && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start space-x-2">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>{executeError}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end space-x-2 px-6 py-3 border-t border-gray-200 flex-shrink-0 bg-gray-50 rounded-b-xl">
                    <button
                        onClick={onClose}
                        disabled={isExecuting}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-semibold"
                    >
                        {executeResult ? 'Lukk' : 'Avbryt'}
                    </button>
                    {!executeResult && onAddToPlan && (
                        <button
                            onClick={() => {
                                const sources: BatchMoveSource[] = lines
                                    .filter(l => l.selected && l.quantity > 0 && l.toBinCode)
                                    .map(l => ({
                                        fromBinCode: l.fromBinCode,
                                        itemNo: l.itemNo,
                                        quantity: l.quantity,
                                        toBinCodeOverride: l.toBinCode
                                    }));
                                onAddToPlan(sources);
                            }}
                            disabled={!canConfirm}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                            title="Legg til som steg i en flytteplan i stedet for å utføre nå"
                        >
                            Legg i plan
                        </button>
                    )}
                    {!executeResult && (
                        <button
                            onClick={handleConfirm}
                            disabled={!canConfirm}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold flex items-center space-x-2"
                        >
                            {isExecuting ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    <span>Oppretter...</span>
                                </>
                            ) : (
                                <span>Utfør nå ({totals.lineCount})</span>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
